import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCustomer } from "@/features/customers/hooks/use-customers";
import { useInvoices } from "@/features/invoices/hooks/use-invoices";
import type { Invoice } from "@/features/invoices/types/invoice.types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Edit, FileText, Euro, Clock, Check, X } from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { RichTextDisplay } from "@/components/ui/rich-text-display";
import { useUpdateCustomer } from "@/features/customers/hooks/use-customers";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useDateFormat } from "@/hooks/use-date-format";

export const Route = createFileRoute("/(app)/dashboard/customers/$id/")({
  component: CustomerDetailPage,
});

function CustomerDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: customer, isLoading } = useCustomer(id);
  const { formatDate } = useDateFormat();
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesContent, setNotesContent] = useState("");
  const updateCustomerMutation = useUpdateCustomer();

  // Initialize notes content when customer data loads
  useEffect(() => {
    if (customer?.notes) {
      setNotesContent(customer.notes);
    }
  }, [customer]);

  const handleSaveNotes = async () => {
    try {
      await updateCustomerMutation.mutateAsync({
        id,
        data: { notes: notesContent },
      });
      setIsEditingNotes(false);
      toast.success(t('customers.detail.notesTab.saved'));
    } catch {
      toast.error(t('customers.detail.notesTab.saveFailed'));
    }
  };

  const handleCancelNotes = () => {
    setNotesContent(customer?.notes || "");
    setIsEditingNotes(false);
  };
  
  // Load customer invoices
  const { data: invoicesData } = useInvoices({ 
    customerId: id,
    limit: 100 
  });

  // Calculate invoice statistics
  const invoiceStats = useMemo(() => {
    if (!invoicesData?.data) {
      return { open: 0, paid: 0, totalRevenue: 0 };
    }

    const invoices = invoicesData.data;
    const open = invoices.filter((inv: Invoice) => inv.status === 'draft' || inv.status === 'sent').length;
    const paid = invoices.filter((inv: Invoice) => inv.status === 'paid').length;
    const totalRevenue = invoices
      .filter((inv: Invoice) => inv.status === 'paid')
      .reduce((sum: number, inv: Invoice) => sum + parseFloat(inv.total || '0'), 0);

    return { open, paid, totalRevenue };
  }, [invoicesData]);

  // Calculate monthly revenue data for chart
  const monthlyRevenueData = useMemo(() => {
    if (!invoicesData?.data) return [];

    const invoices = invoicesData.data.filter((inv: Invoice) => inv.status === 'paid');
    const monthlyData: { [key: string]: number } = {};

    // Get last 12 months
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.push(key);
      monthlyData[key] = 0;
    }

    // Aggregate revenue by month
    invoices.forEach((invoice: Invoice) => {
      if (!invoice.paidAt) return; // Skip if no paid date
      const date = new Date(invoice.paidAt);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (key in monthlyData) {
        monthlyData[key] = (monthlyData[key] || 0) + parseFloat(invoice.total || '0');
      }
    });

    // Convert to chart data format
    return months.map(monthKey => {
      const [year, month] = monthKey.split('-');
      const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
      return {
        month: `${monthNames[parseInt(month || '1') - 1]} ${year?.slice(2) || ''}`,
        revenue: monthlyData[monthKey] || 0,
      };
    });
  }, [invoicesData]);

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <p>{t('customers.detail.loading')}</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="container mx-auto py-6">
        <p>{t('customers.detail.notFound')}</p>
      </div>
    );
  }

  const customerName = customer.customerType === "business" 
    ? customer.companyName 
    : `${customer.firstName} ${customer.lastName}`;

  const primaryEmail = customer.emails.find((e) => e.isPrimary === "true")?.email 
    || customer.emails[0]?.email;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate({ to: "/dashboard/customers" })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{customerName}</h1>
              <Badge variant={customer.customerType === "business" ? "default" : "secondary"}>
                {t(`customers.types.${customer.customerType}`)}
              </Badge>
            </div>
            <div className="text-muted-foreground space-y-1 mt-2">
              {customer.customerType === "business" && customer.companyName && (
                <p>{t('customers.detail.customerNumber')}: {customer.customerNumber}</p>
              )}
              {customer.vatId && (
                <p>{t('customers.detail.vatIdLabel')}: {customer.vatId}</p>
              )}
            </div>
          </div>
        </div>
        <Button onClick={() => navigate({ to: `/dashboard/customers/edit/${id}` })}>
          <Edit className="mr-2 h-4 w-4" />
          {t('customers.detail.editButton')}
        </Button>
      </div>

      {/* Monthly Revenue Chart */}
      <Card>
        <CardHeader>
          <CardTitle>{t('customers.detail.monthlyRevenueChart.title')}</CardTitle>
          <CardDescription>{t('customers.detail.monthlyRevenueChart.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="month" 
                  className="text-xs"
                  tick={{ fill: 'var(--muted-foreground)' }}
                />
                <YAxis 
                  className="text-xs"
                  tick={{ fill: 'var(--muted-foreground)' }}
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value: number) => [
                    new Intl.NumberFormat('de-DE', { 
                      style: 'currency', 
                      currency: 'EUR' 
                    }).format(value),
                    t('customers.detail.monthlyRevenueChart.revenue')
                  ]}
                  contentStyle={{
                    backgroundColor: 'var(--background)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                  }}
                  labelStyle={{ color: 'var(--foreground)' }}
                />
                <Bar 
                  dataKey="revenue" 
                  fill="var(--primary)" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('customers.detail.stats.openInvoices')}</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{invoiceStats.open}</div>
            <p className="text-xs text-muted-foreground">{t('customers.detail.stats.openInvoicesDescription')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('customers.detail.stats.paidInvoices')}</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{invoiceStats.paid}</div>
            <p className="text-xs text-muted-foreground">{t('customers.detail.stats.paidInvoicesDescription')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('customers.detail.stats.totalRevenue')}</CardTitle>
            <Euro className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Intl.NumberFormat('de-DE', { 
                style: 'currency', 
                currency: 'EUR' 
              }).format(invoiceStats.totalRevenue)}
            </div>
            <p className="text-xs text-muted-foreground">{t('customers.detail.stats.totalRevenueDescription')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="information" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="information">{t('customers.detail.tabs.information')}</TabsTrigger>
          <TabsTrigger value="invoices">{t('customers.detail.tabs.invoices')}</TabsTrigger>
          <TabsTrigger value="quotes">{t('customers.detail.tabs.quotes')}</TabsTrigger>
          <TabsTrigger value="notes">{t('customers.detail.tabs.notes')}</TabsTrigger>
        </TabsList>

        {/* Information Tab */}
        <TabsContent value="information" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Rechnungsadresse */}
            <Card>
              <CardHeader>
                <CardTitle>{t('customers.detail.billingAddress')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {customer.customerType === "business" ? (
                  <p className="font-medium">{customer.companyName}</p>
                ) : (
                  <p className="font-medium">{customer.firstName} {customer.lastName}</p>
                )}
                {customer.billingStreet && <p>{customer.billingStreet}</p>}
                {(customer.billingPostalCode || customer.billingCity) && (
                  <p>{customer.billingPostalCode} {customer.billingCity}</p>
                )}
                {customer.billingCountry && <p>{customer.billingCountry}</p>}
              </CardContent>
            </Card>

            {/* Lieferadresse */}
            {(customer.shippingStreet || customer.shippingCity || customer.shippingPostalCode) && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('customers.detail.shippingAddress')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {customer.shippingStreet && <p>{customer.shippingStreet}</p>}
                  {(customer.shippingPostalCode || customer.shippingCity) && (
                    <p>{customer.shippingPostalCode} {customer.shippingCity}</p>
                  )}
                  {customer.shippingCountry && <p>{customer.shippingCountry}</p>}
                </CardContent>
              </Card>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Kontaktdaten */}
            <Card>
              <CardHeader>
                <CardTitle>{t('customers.detail.contactData')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('customers.detail.email')}</p>
                  <p>{primaryEmail || "-"}</p>
                  {customer.emails.length > 1 && (
                    <div className="mt-2 space-y-1">
                      {customer.emails.filter((e) => e.isPrimary !== "true").map((email) => (
                        <p key={email.id} className="text-sm text-muted-foreground">
                          {email.email}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('customers.detail.phone')}</p>
                  <p>{customer.phone || "-"}</p>
                </div>
              </CardContent>
            </Card>

            {/* Geschäftsdaten */}
            <Card>
              <CardHeader>
                <CardTitle>{t('customers.detail.businessData')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('customers.detail.paymentTerms')}</p>
                  <p>{t('customers.detail.paymentTermsDays', { days: customer.paymentTerms || 14 })}</p>
                </div>
                {customer.customerType === "business" && (
                  <>
                    {customer.vatId && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('customers.detail.vatId')}</p>
                        <p>{customer.vatId}</p>
                      </div>
                    )}
                    {customer.taxNumber && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('customers.detail.taxNumber')}</p>
                        <p>{customer.taxNumber}</p>
                      </div>
                    )}
                    {customer.leitwegId && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('customers.detail.leitwegId')}</p>
                        <p>{customer.leitwegId}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Rechnungen Tab */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('customers.detail.invoicesTab.title')}</CardTitle>
                <CardDescription>
                  {t('customers.detail.invoicesTab.description')}
                </CardDescription>
              </div>
              <Button 
                onClick={() => navigate({ to: '/dashboard/invoices/new', search: { customerId: id } })}
              >
                <FileText className="mr-2 h-4 w-4" />
                {t('customers.detail.invoicesTab.createInvoice')}
              </Button>
            </CardHeader>
            <CardContent>
              {!invoicesData?.data || invoicesData.data.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  {t('customers.detail.invoicesTab.empty')}
                </p>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('customers.detail.invoicesTab.invoiceNumber')}</TableHead>
                        <TableHead>{t('customers.detail.invoicesTab.date')}</TableHead>
                        <TableHead>{t('customers.detail.invoicesTab.dueDate')}</TableHead>
                        <TableHead className="text-right">{t('customers.detail.invoicesTab.amount')}</TableHead>
                        <TableHead>{t('customers.detail.invoicesTab.status')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoicesData.data.map((invoice: Invoice) => (
                        <TableRow
                          key={invoice.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate({ to: `/dashboard/invoices/edit/${invoice.id}` })}
                        >
                          <TableCell className="font-medium">
                            {invoice.invoiceNumber || t('customers.detail.invoicesTab.draft')}
                          </TableCell>
                          <TableCell>
                            {invoice.invoiceDate
                              ? formatDate(new Date(invoice.invoiceDate))
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {invoice.dueDate
                              ? formatDate(new Date(invoice.dueDate))
                              : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {parseFloat(invoice.total || '0').toLocaleString('de-DE', {
                              style: 'currency',
                              currency: 'EUR',
                            })}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                invoice.status === 'paid'
                                  ? 'default'
                                  : invoice.status === 'sent'
                                  ? 'secondary'
                                  : invoice.status === 'cancelled'
                                  ? 'destructive'
                                  : 'outline'
                              }
                            >
                              {t(`invoiceStatus.${invoice.status}`)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Angebote Tab */}
        <TabsContent value="quotes">
          <Card>
            <CardHeader>
              <CardTitle>{t('customers.detail.quotesTab.title')}</CardTitle>
              <CardDescription>
                {t('customers.detail.quotesTab.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center py-8">
                {t('customers.detail.quotesTab.empty')}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notizen Tab */}
        <TabsContent value="notes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('customers.detail.notesTab.title')}</CardTitle>
                <CardDescription>
                  {t('customers.detail.notesTab.description')}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {isEditingNotes ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelNotes}
                    >
                      <X className="mr-2 h-4 w-4" />
                      {t('customers.detail.notesTab.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveNotes}
                      disabled={updateCustomerMutation.isPending}
                    >
                      <Check className="mr-2 h-4 w-4" />
                      {t('customers.detail.notesTab.save')}
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsEditingNotes(true)}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    {t('customers.detail.notesTab.edit')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isEditingNotes ? (
                <RichTextEditor
                  content={notesContent}
                  onChange={setNotesContent}
                  placeholder={t('customers.detail.notesTab.placeholder')}
                  className="min-h-[400px]"
                />
              ) : (
                <div className="min-h-[200px]">
                  {notesContent ? (
                    <RichTextDisplay content={notesContent} />
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      {t('customers.detail.notesTab.empty')}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
